<template>
  <div class="task">
    <div class="task-items" v-loading="taskLoading">
      <el-card v-for="task in taskList" :key="task.taskId" shadow="hover" class="task-item">
        <div slot="header">
          <div class="task-header">
            <div class="task-header-info">
              <span>
                任务时间：
                <em>{{ moment(task.addTime).format('YYYY-MM-DD HH:mm:ss') }}</em>
              </span>
              <span>
                任务名称：<em>{{ task.taskName }}</em>
              </span>
              <span>
                任务ID：<em>{{ task.taskId }}</em>
              </span>
              <span>
                创建人：<em>{{ task.createOpName }}</em>
              </span>
              <span>
                任务进度：<em>
                  {{ (task.successNum || 0) + (task.failedNum || 0) }}/{{ task.taskNum }} 成功{{ task.successNum || 0 }}，失败{{
                    task.failedNum || 0
                  }}
                </em>
              </span>
            </div>
          </div>
        </div>

        <div class="material-items">
          <template v-if="task.childTask && task.childTask.length">
            <div v-for="o in task.childTask" :key="o.taskId" class="material-item">
              <div class="checkbox">
                <el-checkbox :disabled="o.pass === 1 || o.taskStatus !== TASK_STATUS.COMPLETED" v-model="o.isCheck"></el-checkbox>
              </div>
              <div style="display: flex; flex-flow: column; height: 100%">
                <div class="material" @mouseenter="onGridElementMouseEnter(o)" @mouseleave="onGridElementMouseLeave(o)">
                  <video
                    v-if="o.videoVisible"
                    style="width: 220px; height: 240px"
                    :poster="o.posterUrl"
                    preload="none"
                    controls
                    :src="o.url"
                  ></video>
                  <el-image v-else style="width: 220px; height: 240px" lazy :src="o.posterUrl" fit="contain">
                    <template #error v-if="o.taskStatus !== TASK_STATUS.COMPLETED">
                      <div class="error-msg">
                        <el-tooltip v-if="o.taskResult" class="item" effect="dark" :content="o.taskResult" placement="top">
                          <span>{{ statusTexts[o.taskStatus] }}</span>
                        </el-tooltip>
                        <span v-else>{{ statusTexts[o.taskStatus] }}</span>
                      </div>
                    </template>
                  </el-image>
                </div>

                <div class="material-info">
                  <el-tooltip class="item" effect="dark" :content="o.name" placement="top">
                    <span>
                      <el-tag v-if="o.pass === 1">已入库</el-tag>
                      素材名称：{{ o.name }}
                    </span>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>
          <span v-else>暂无子任务</span>
        </div>
      </el-card>
    </div>

    <div class="bottom-row">
      <div class="refresh" @click="getTaskList">
        <span v-if="taskLoading" class="el-icon-loading"></span>
        <span v-else class="el-icon-refresh"></span>
        <span>刷新</span>
      </div>

      <el-pagination
        class="pager"
        background
        layout="total,  prev, pager, next, jumper"
        @current-change="getTaskList"
        :total="pagerInfo.total"
        :current-page.sync="pagerInfo.pageIndex"
      >
      </el-pagination>
    </div>

    <AuditModal :visible.sync="auditModal" :id-list="currTasks.map((task) => task.id)" @audit-finished="getTaskList"></AuditModal>
  </div>
</template>

<script setup>
import { ref, set } from 'vue'
import { Message } from 'view-design'
import moment from 'moment'
import AuditModal from '@/views/material/library/components/audit-modal.vue'
import { useVideoElementDisplay } from '@/components/material-selector/hooks/useSelector'
import { creativeEditorTaskList } from '@/api/creative.model.js'
import { TASK_STATUS } from '../../const'
const auditModal = ref(false)
const taskList = ref([])

const statusTexts = ['未开始', '生成中', '已完成', '失败', '终止']
const currTasks = ref([])
const pagerInfo = ref({
  pageIndex: 1,
  pageSize: 10,
  total: 0
})
const taskLoading = ref(false)

function onQuery() {
  pagerInfo.value.pageIndex = 1
  getTaskList()
}

function getTaskList() {
  taskLoading.value = true
  const { pageIndex, pageSize } = pagerInfo.value
  creativeEditorTaskList({ pageIndex, pageSize })
    .then((data) => {
      taskLoading.value = false
      const { list, page_info } = data
      pagerInfo.value.total = page_info?.total_number || 0
      if (list?.length) {
        taskList.value = list.map((i) => {
          return {
            ...i,
            childTask: i.childTask?.map((j) => {
              return {
                ...j,
                isCheck: false
              }
            })
          }
        })
      }
    })
    .finally(() => {
      taskLoading.value = false
    })
}

onQuery()
const { onGridElementMouseEnter, onGridElementMouseLeave } = useVideoElementDisplay()

// eslint-disable-next-line
defineExpose({ onQuery })
</script>

<style scoped lang="scss">
@import '../../styles/index.scss';

.task {
  width: calc(80% - $gap);
  margin-left: $gap;
  min-width: 1200px;
  height: $height;
  @include bg;

  .bottom-row {
    display: flex;
    align-items: center;
    border-top: 1px solid #f4f4f4;

    .refresh {
      padding: 0 $gap;
      font-size: 16px;
      cursor: pointer;
    }
    .pager {
      flex: 1;
      padding: 10px;
      text-align: right;
    }
  }

  .task-items {
    padding: $gap;
    overflow: auto;
    height: calc($height - 52px);

    .task-item {
      margin-bottom: $gap;

      ::v-deep .el-card__header {
        background-color: $bg;
        padding: 14px 20px;
      }

      ::v-deep .el-card__body {
        padding: $gap;
      }

      .task-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &-info {
          font-size: 14px;
          display: flex;
          align-items: center;
          flex: 1;
          span {
            margin-right: 10px;
            font-weight: 700;
            em {
            }
          }
        }

        &-opts {
          min-width: 80px;
        }
      }

      .material-items {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: $gap;
        overflow: auto;
        padding-bottom: 4px;

        .material-item {
          flex: 0 0 220px;
          height: 270px;
          background-color: rgb(247, 247, 247);
          text-align: center;
          border-radius: 4px;
          overflow: hidden;
          position: relative;

          .checkbox {
            position: absolute;
            right: 10px;
            top: 6px;
            z-index: 1;
          }

          .material-info {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            background: #e9e9e9;
            padding: 0 5px;
            color: #333;
            line-height: 30px;
          }

          .material {
            display: flex;
            flex: 1;
            align-items: center;
            margin: 0 auto;
          }

          ::v-deep .error-msg {
            height: 100%;
            display: flex;
            align-items: center;
            text-align: center;
            word-break: break-all;
            span {
              margin: 0 auto;
            }
          }
        }
      }
    }
  }
}
</style>
